import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAppContext, Product } from '../../context/AppContext';
import { useBrand } from '../../context/BrandContext';
import { useCopy } from '../../context/CopyContext';
import { useTESEvents } from '../../hooks/useTESEvents';
import Header from '../Header';
import Heading from '../Heading';
import Button from '../Button';
import PoweredBy from '../PoweredBy';
import ItemsModalView from './ItemsModalView';

interface Detection {
  class_id: number;
  class_name: string;
  confidence: number;
  bbox: [number, number, number, number];
}

interface LocationState {
  detections: Detection[];
  detectedProducts: Product[];
}

// Stacked product with quantity
interface StackedProduct {
  product: Product;
  quantity: number;
  avgConfidence: number;
  originalIndices: number[]; // Track original indices for exclusion
}

// Hide native scrollbar
const hideNativeScrollbar = `
  .custom-scroll-container::-webkit-scrollbar {
    display: none;
  }
  .custom-scroll-container {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

const MultipleItemsDetectedView: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, dispatch } = useAppContext();
  const { brandConfig, isDarkMode } = useBrand();
  const { getCopy } = useCopy();
  const { emitBatchProductsDetected, isEnabled: tesEnabled } = useTESEvents();
  const [showItemsModal, setShowItemsModal] = useState(false);
  const [excludedStacks, setExcludedStacks] = useState<Set<string>>(new Set()); // Track by product ID

  // Custom scrollbar state
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrollThumbHeight, setScrollThumbHeight] = useState(0);
  const [scrollThumbTop, setScrollThumbTop] = useState(0);
  const [isScrollable, setIsScrollable] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartY = useRef(0);
  const dragStartScrollTop = useRef(0);

  const locationState = location.state as LocationState | null;
  const detectedProducts = locationState?.detectedProducts || [];

  // Stack duplicate products by ID
  const stackedProducts: StackedProduct[] = React.useMemo(() => {
    const productMap = new Map<string, StackedProduct>();

    detectedProducts.forEach((product, index) => {
      const existing = productMap.get(product.id);
      if (existing) {
        existing.quantity += 1;
        existing.avgConfidence = (existing.avgConfidence * (existing.quantity - 1) + (product.detectionConfidence || 0)) / existing.quantity;
        existing.originalIndices.push(index);
      } else {
        productMap.set(product.id, {
          product,
          quantity: 1,
          avgConfidence: product.detectionConfidence || 0,
          originalIndices: [index]
        });
      }
    });

    return Array.from(productMap.values());
  }, [detectedProducts]);

  // Calculate total items (accounting for quantities)
  const totalDetectedItems = detectedProducts.length;

  // Filter out excluded stacks and calculate included items
  const includedStacks = stackedProducts.filter(stack => !excludedStacks.has(stack.product.id));
  const includedItemCount = includedStacks.reduce((sum, stack) => sum + stack.quantity, 0);

  // Build the flat list of included products for processing
  const includedProducts = React.useMemo(() => {
    const products: Product[] = [];
    includedStacks.forEach(stack => {
      for (let i = 0; i < stack.quantity; i++) {
        products.push(stack.product);
      }
    });
    return products;
  }, [includedStacks]);

  // Custom scrollbar calculation
  const updateScrollbar = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollHeight, clientHeight, scrollTop } = container;
    const canScroll = scrollHeight > clientHeight;
    setIsScrollable(canScroll);

    if (canScroll) {
      const trackHeight = clientHeight - 16; // Account for padding
      const thumbHeight = Math.max(60, (clientHeight / scrollHeight) * trackHeight);
      const maxScrollTop = scrollHeight - clientHeight;
      const thumbTop = maxScrollTop > 0 ? (scrollTop / maxScrollTop) * (trackHeight - thumbHeight) : 0;

      setScrollThumbHeight(thumbHeight);
      setScrollThumbTop(thumbTop + 8); // Add top padding
    }
  }, []);

  // Update scrollbar on scroll
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => updateScrollbar();
    container.addEventListener('scroll', handleScroll);

    // Initial calculation
    updateScrollbar();

    // Recalculate on resize
    const resizeObserver = new ResizeObserver(updateScrollbar);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
    };
  }, [updateScrollbar, stackedProducts]);

  // Handle scrollbar thumb drag
  const handleThumbMouseDown = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setIsDragging(true);
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    dragStartY.current = clientY;
    dragStartScrollTop.current = scrollContainerRef.current?.scrollTop || 0;
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMove = (e: MouseEvent | TouchEvent) => {
      const container = scrollContainerRef.current;
      if (!container) return;

      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
      const deltaY = clientY - dragStartY.current;

      const { scrollHeight, clientHeight } = container;
      const trackHeight = clientHeight - 16;
      const thumbHeight = Math.max(60, (clientHeight / scrollHeight) * trackHeight);
      const maxThumbTop = trackHeight - thumbHeight;
      const maxScrollTop = scrollHeight - clientHeight;

      const scrollRatio = maxScrollTop / maxThumbTop;
      container.scrollTop = dragStartScrollTop.current + (deltaY * scrollRatio);
    };

    const handleUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    document.addEventListener('touchmove', handleMove);
    document.addEventListener('touchend', handleUp);

    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleUp);
    };
  }, [isDragging]);

  // Handle track click to jump scroll
  const handleTrackClick = (e: React.MouseEvent) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const track = e.currentTarget;
    const rect = track.getBoundingClientRect();
    const clickY = e.clientY - rect.top - 8; // Account for padding

    const { scrollHeight, clientHeight } = container;
    const trackHeight = clientHeight - 16;
    const thumbHeight = Math.max(60, (clientHeight / scrollHeight) * trackHeight);
    const maxThumbTop = trackHeight - thumbHeight;
    const maxScrollTop = scrollHeight - clientHeight;

    const targetThumbTop = clickY - thumbHeight / 2;
    const clampedThumbTop = Math.max(0, Math.min(targetThumbTop, maxThumbTop));
    container.scrollTop = (clampedThumbTop / maxThumbTop) * maxScrollTop;
  };

  const toggleExcludeStack = (productId: string) => {
    setExcludedStacks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const handleStartProcessing = async () => {
    if (includedProducts.length > 0) {
      // Emit TES events for all detected products (async, non-blocking)
      if (tesEnabled) {
        emitBatchProductsDetected(includedProducts).catch((err) => {
          console.error('[TES] Batch detection emit failed:', err);
        });
      }

      const firstProduct = includedProducts[0];
      const remainingProducts = includedProducts.slice(1);

      dispatch({ type: 'PREVIEW_PRODUCT', payload: firstProduct });
      dispatch({ type: 'SET_STEP', payload: 5 });

      navigate('/empty-accepted', {
        state: {
          detectedProduct: firstProduct,
          isMultiItemFlow: true,
          totalItems: includedProducts.length,
          currentItemIndex: 0,
          remainingItems: remainingProducts
        }
      });
    }
  };

  const handleCancel = () => {
    navigate('/add-empty');
  };

  const handleItemsClick = () => {
    setShowItemsModal(true);
  };

  const handleCloseItemsModal = () => {
    setShowItemsModal(false);
  };

  return (
    <div
      className="bg-brand-background h-screen flex flex-col relative overflow-hidden"
      data-node-id="multiple-items-detected"
    >
      {/* Hide native scrollbar */}
      <style>{hideNativeScrollbar}</style>
      {/* Animated Teal Glow Effect at Bottom */}
      <motion.div
        className="absolute bottom-[-675px] left-0 w-[1080px] h-[1080px] rounded-[1000px] pointer-events-none"
        style={{
          background: brandConfig.colors.primary[isDarkMode ? 'dark' : 'light'],
          filter: 'blur(200px)',
        }}
        animate={{
          y: [0, -100, 0],
          opacity: [.25, .75, .25],
          scale: [1, 1.25, 1],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />

      {/* Content Container - fixed height layout */}
      <div className="relative z-10 flex flex-col h-full overflow-hidden">
        {/* Header Section - fixed at top */}
        <div className="flex-shrink-0 flex justify-center mx-20 mt-20">
          <div className="w-full max-w-[920px]">
            <Header
              showItems={state.itemsCount > 0}
              itemsNumber={state.itemsCount.toString()}
              onItemsClick={handleItemsClick}
            />
          </div>
        </div>

        {/* Main Content Area - flexible with overflow scroll */}
        <motion.div
          className="flex-1 flex flex-col mx-20 mt-12 min-h-0 overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {/* Heading - fixed height */}
          <div className="flex-shrink-0 flex items-center justify-center mb-8">
            <div className="text-center w-full max-w-[920px]">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: 0.6,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
              >
                <Heading
                  title={`We detected ${totalDetectedItems} ${totalDetectedItems === 1 ? 'item' : 'items'}!`}
                  subtitle={includedItemCount < totalDetectedItems ? `${includedItemCount} selected for processing` : undefined}
                />
              </motion.div>
            </div>
          </div>

          {/* Scrollable Product List with Custom Scrollbar */}
          <div className="flex-1 flex select-none relative min-h-0 overflow-hidden">
            {/* Scrollable content */}
            <div
              ref={scrollContainerRef}
              className="flex-1 custom-scroll-container"
              style={{
                overflowY: 'auto',
                paddingRight: isScrollable ? '24px' : '0',
              }}
            >
              <div className="w-full max-w-[920px] mx-auto space-y-4 pb-4 px-4">
              {stackedProducts.map((stack, index) => {
                const isExcluded = excludedStacks.has(stack.product.id);
                return (
                  <div
                    key={stack.product.id}
                    className={`flex items-center gap-6 p-6 rounded-3xl border-2 transition-all duration-300 ${
                      isExcluded
                        ? 'bg-gray-200 border-gray-300'
                        : 'bg-brand-surface border-brand-border'
                    }`}
                  >
                    {/* Checkbox - X icon with larger click area */}
                    <button
                      onClick={() => toggleExcludeStack(stack.product.id)}
                      className="flex-shrink-0 w-12 h-12 flex items-center justify-center transition-colors hover:bg-brand-background/50 rounded-lg"
                      aria-label={isExcluded ? "Include item" : "Exclude item"}
                    >
                      <svg
                        className={`w-6 h-6 transition-colors ${isExcluded ? 'text-red-500' : 'text-brand-text-secondary/40'}`}
                        viewBox="0 0 20 20"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <path d="M4 4L16 16M16 4L4 16" strokeLinecap="round" />
                      </svg>
                    </button>

                    {/* Product Image with Quantity Badge */}
                    <div className={`relative w-20 h-20 flex-shrink-0 ${isExcluded ? 'opacity-50' : ''}`}>
                      <div className="w-full h-full rounded-xl overflow-hidden bg-brand-background">
                        <img
                          src={stack.product.image || '/api/placeholder/80/80'}
                          alt={stack.product.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      {/* Quantity Badge - only show if more than 1 */}
                      {stack.quantity > 1 && (
                        <div className={`absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center shadow-lg ${isExcluded ? 'bg-gray-400' : 'bg-brand-primary'}`}>
                          <span className="font-brand-secondary font-bold text-sm text-white">
                            {stack.quantity}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Product Info */}
                    <div className="flex-1">
                      <div className={`font-brand-primary font-normal text-2xl mb-1 ${isExcluded ? 'text-gray-400 line-through' : 'text-brand-text-primary'}`}>
                        {stack.product.brand}
                      </div>
                      <div className={`font-brand-secondary font-light text-xl ${isExcluded ? 'text-gray-400' : 'text-brand-text-secondary'}`}>
                        {stack.product.name}
                      </div>
                    </div>

                    {/* Confidence Badge */}
                    <div className={`px-4 py-2 rounded-lg border ${isExcluded ? 'bg-gray-100 border-gray-300' : 'bg-brand-background border-brand-border'}`}>
                      <span className={`font-brand-secondary font-semibold text-sm ${isExcluded ? 'text-gray-400' : 'text-brand-text-secondary'}`}>
                        {Math.round(stack.avgConfidence * 100)}%
                      </span>
                    </div>
                  </div>
                );
              })}
              </div>
            </div>

            {/* Custom Scrollbar - thick and touch-friendly */}
            {isScrollable && (
              <div
                className="absolute right-0 top-0 bottom-0 w-6 cursor-pointer"
                onClick={handleTrackClick}
                style={{
                  background: 'rgba(200, 200, 200, 0.5)',
                  borderRadius: '12px',
                }}
              >
                {/* Scrollbar thumb */}
                <div
                  className="absolute w-5 cursor-grab active:cursor-grabbing"
                  style={{
                    height: `${scrollThumbHeight}px`,
                    top: `${scrollThumbTop}px`,
                    left: '2px',
                    background: 'linear-gradient(180deg, #666 0%, #444 100%)',
                    borderRadius: '10px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                    touchAction: 'none',
                  }}
                  onMouseDown={handleThumbMouseDown}
                  onTouchStart={handleThumbMouseDown}
                />
              </div>
            )}
          </div>
        </motion.div>

        {/* Bottom Section - fixed at bottom */}
        <div className="flex-shrink-0 flex flex-col gap-8 items-center mx-20 mb-10 mt-8">
          {/* Buttons */}
          <div className="flex flex-row gap-6 lg:gap-10 w-full max-w-[920px]">
            <Button variant="secondary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleStartProcessing}>
              Process Items
            </Button>
          </div>

          {/* Footer */}
          <div className="flex justify-center">
            <PoweredBy />
          </div>
        </div>
      </div>

      {/* Items Modal */}
      <ItemsModalView
        isOpen={showItemsModal}
        onClose={handleCloseItemsModal}
      />
    </div>
  );
};

export default MultipleItemsDetectedView;
